/**
 * Cost Overview Display Order Service
 * Manages the order in which cost items appear in the calculator cost overview.
 */

import { supabase } from "../lib/supabase";

export interface CostOverviewOrderItem {
  id?: number;
  item_key: string;
  display_label: string;
  sort_order: number;
}

export async function fetchCostOverviewDisplayOrder(): Promise<
  CostOverviewOrderItem[]
> {
  const { data, error } = await supabase
    .from("cost_overview_display_order")
    .select("id, item_key, display_label, sort_order")
    .order("sort_order", { ascending: true });

  if (error) {
    console.error("Error fetching cost overview display order:", error);
    return [];
  }
  return (data || []) as CostOverviewOrderItem[];
}

export async function saveCostOverviewDisplayOrder(
  items: CostOverviewOrderItem[],
): Promise<{ success: boolean; error?: string }> {
  try {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const { error } = await supabase
        .from("cost_overview_display_order")
        .upsert(
          {
            item_key: item.item_key,
            display_label: item.display_label,
            sort_order: i,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "item_key" },
        );

      if (error) {
        console.error("Error saving cost overview display order:", error);
        return { success: false, error: error.message };
      }
    }
    return { success: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Unknown error";
    return { success: false, error: msg };
  }
}

/**
 * Returns a Map of item_key -> sort_order for use when sorting components.
 * Item key format: "transport" | "lmd" | "lmd_surcharge" | "lmd_nl_pallet_surcharge" | "export_clearance" | "pickup_fee" | "commodity_inspection" | "post_export_license"
 *   | "additional_cost_{id}" | "pricing_component_{id}"
 */
export async function getCostOverviewSortMap(): Promise<Map<string, number>> {
  const items = await fetchCostOverviewDisplayOrder();
  const map = new Map<string, number>();
  items.forEach((item) => {
    map.set(item.item_key, item.sort_order);
  });
  return map;
}

/** Default sort position when a new LMD line is not yet in cost_overview_display_order. */
export function getFallbackSortOrder(
  key: string,
  sortMap: Map<string, number> | null,
): number {
  if (sortMap?.has(key)) return sortMap.get(key)!;

  if (key === "lmd_nl_pallet_surcharge" && sortMap) {
    const dieselOrder = sortMap.get("lmd_surcharge");
    if (dieselOrder != null) return dieselOrder + 1;
    const lmdOrder = sortMap.get("lmd");
    if (lmdOrder != null) return lmdOrder + 1;
  }

  if (key === "europallet_non_exchange" && sortMap) {
    const tolOrder = sortMap.get("lmd_nl_pallet_surcharge");
    if (tolOrder != null) return tolOrder + 1;
    const dieselOrder = sortMap.get("lmd_surcharge");
    if (dieselOrder != null) return dieselOrder + 1;
    const lmdOrder = sortMap.get("lmd");
    if (lmdOrder != null) return lmdOrder + 1;
  }

  return 9999;
}

/**
 * Derives the sort key for a component from the pricing result / invoice line.
 */
export function getComponentSortKey(component: {
  componentType?: string;
  componentId?: number;
  name?: string;
}): string {
  const rawType = (component.componentType || "").toLowerCase();
  const id = component.componentId;

  // Invoice wizard cost_types → calculator item keys
  const invoiceTypeMap: Record<string, string> = {
    tariff: "transport",
    transport: "transport",
    lmd_delivery: "lmd",
    lmd: "lmd",
    lmd_diesel_surcharge: "lmd_surcharge",
    lmd_surcharge: "lmd_surcharge",
    lmd_nl_pallet_surcharge: "lmd_nl_pallet_surcharge",
    europallet_non_exchange: "europallet_non_exchange",
  };
  if (invoiceTypeMap[rawType]) return invoiceTypeMap[rawType];

  // Numeric cost_type = pricing_components.id (common on invoice lines)
  if (/^\d+$/.test(rawType)) return `pricing_component_${rawType}`;

  const type = rawType;
  if (type === "transport") return "transport";
  if (type === "lmd") return "lmd";
  if (type === "lmd_surcharge") return "lmd_surcharge";
  if (type === "lmd_nl_pallet_surcharge") return "lmd_nl_pallet_surcharge";
  if (type === "containervervoer_plaats_van_bestemming")
    return "containervervoer_plaats_van_bestemming";
  if (type === "export_clearance") return "export_clearance";
  if (type === "pickup_fee") return "pickup_fee";
  if (type === "commodity_inspection") return "commodity_inspection";
  if (type === "post_export_license") return "post_export_license";
  if (type === "europallet_non_exchange") return "europallet_non_exchange";
  if (type === "customs_btw") return "customs_btw";
  if (type === "customs_duties") return "customs_duties";
  if (type === "customs_commission") return "customs_commission";
  if (type === "customs_commission_btw") return "customs_commission_btw";
  if (type === "customs_commission_duties") return "customs_commission_duties";
  if (type === "additional_cost" && id != null) return `additional_cost_${id}`;

  const name = (component.name || "").toLowerCase();
  if (name.includes("containervervoer") && name.includes("bestemming"))
    return "containervervoer_plaats_van_bestemming";
  if (
    name.includes("transport") ||
    name.includes("freight") ||
    name.includes("tariff") ||
    (name.includes("vervoer") && !name.includes("containervervoer")) ||
    name.includes("vracht")
  )
    return "transport";
  if (name.includes("lmd") && name.includes("diesel")) return "lmd_surcharge";
  if (
    name.includes("tol") ||
    (name.includes("lmd") && name.includes("nl") && name.includes("pallet"))
  )
    return "lmd_nl_pallet_surcharge";
  if (name.includes("lmd")) return "lmd";
  if (name.includes("export") && name.includes("clearance")) return "export_clearance";
  if (name.includes("pickup") || name.includes("pick-up") || name.includes("pick up"))
    return "pickup_fee";
  if (name.includes("commodity") || name.includes("inspection") || name.includes("iscm"))
    return "commodity_inspection";
  if (name.includes("post export") || name.includes("post_export"))
    return "post_export_license";
  if (name.includes("europallet") || (name.includes("pallet") && name.includes("geruild")))
    return "europallet_non_exchange";
  if (name.includes("btw") && name.includes("invoer") && !name.includes("voorschot"))
    return "customs_btw";
  if (name.includes("invoerrechten") && !name.includes("voorschot"))
    return "customs_duties";
  // Combined 4% over BTW + duties (single line)
  if (
    name.includes("voorschotprovisie") &&
    name.includes("btw") &&
    (name.includes("invoerrechten") || name.includes("rechten"))
  ) {
    return "customs_commission";
  }
  if ((name.includes("voorschotprovisie") || name.includes("commission")) && name.includes("btw"))
    return "customs_commission_btw";
  if ((name.includes("voorschotprovisie") || name.includes("commission")) && name.includes("invoerrechten"))
    return "customs_commission_duties";
  if (name.includes("voorschotprovisie") || name.includes("commission"))
    return "customs_commission";

  // Positive IDs only — negative IDs are synthetic LMD/transport lines (e.g. -3 NL pallet surcharge).
  if (id != null && id > 0) return `pricing_component_${id}`;

  return `unknown_${id ?? 0}`;
}

/** Strip qty/price suffix: "Pickup Fee (1 × €160.00)" → "Pickup Fee" */
function invoiceLineBaseName(description: string | undefined): string {
  return (description || "").split(" (")[0].trim().toLowerCase();
}

/**
 * Sort key for an invoice line (wizard / Moneybird / preview).
 * Resolves against kostenoverzicht labels and pricing component names so order
 * matches /admin/pricing → Kostenoverzicht volgorde.
 */
export function getInvoiceLineSortKey(
  line: { cost_type?: string; description?: string },
  opts?: {
    orderItems?: CostOverviewOrderItem[];
    pricingComponents?: { id: number; name: string }[];
  }
): string {
  const costType = (line.cost_type || "").trim();
  const baseName = invoiceLineBaseName(line.description);

  // Numeric cost_type = pricing_components.id
  if (/^\d+$/.test(costType)) {
    return `pricing_component_${costType}`;
  }

  // Known invoice/system cost_types (tariff, pickup_fee, lmd_*, customs_*, …)
  if (costType && costType !== "pricing_component" && costType !== "additional_cost") {
    const fromType = getComponentSortKey({
      componentType: costType,
      name: line.description,
    });
    if (!fromType.startsWith("unknown_")) return fromType;
  }

  // Match kostenoverzicht display_label (longest first — "Export Clearance License" before "Export Clearance")
  const orderItems = opts?.orderItems;
  if (orderItems?.length && baseName) {
    const byLabel = [...orderItems].sort(
      (a, b) => b.display_label.length - a.display_label.length
    );
    for (const item of byLabel) {
      const label = item.display_label.trim().toLowerCase();
      if (!label) continue;
      if (baseName === label || baseName.startsWith(label)) {
        return item.item_key;
      }
    }
  }

  // Match pricing_components.name → pricing_component_{id}
  const components = opts?.pricingComponents;
  if (components?.length && baseName) {
    const exact = components.find(
      (c) => c.name.trim().toLowerCase() === baseName
    );
    if (exact) return `pricing_component_${exact.id}`;
    const fuzzy = [...components]
      .sort((a, b) => b.name.length - a.name.length)
      .find((c) => {
        const n = c.name.trim().toLowerCase();
        return n && (baseName.startsWith(n) || n.startsWith(baseName));
      });
    if (fuzzy) return `pricing_component_${fuzzy.id}`;
  }

  return getComponentSortKey({
    componentType: costType,
    name: line.description,
  });
}

/** Sort items using the calculator cost-overview display order. */
export function sortByCostOverviewOrder<
  T extends { componentType?: string; componentId?: number; name?: string },
>(items: T[], sortMap: Map<string, number> | null): T[] {
  return [...items].sort((a, b) => {
    const keyA = getComponentSortKey(a);
    const keyB = getComponentSortKey(b);
    const orderA = getFallbackSortOrder(keyA, sortMap);
    const orderB = getFallbackSortOrder(keyB, sortMap);
    if (orderA !== orderB) return orderA - orderB;
    return keyA.localeCompare(keyB);
  });
}

/** Sort invoice lines like the calculator kostenoverzicht. */
export async function sortInvoiceLinesByCostOverviewOrder<
  T extends { cost_type?: string; description?: string },
>(
  lines: T[],
  pricingComponents?: { id: number; name: string }[]
): Promise<T[]> {
  if (!lines?.length) return lines;
  const orderItems = await fetchCostOverviewDisplayOrder();
  const sortMap = new Map<string, number>();
  orderItems.forEach((item) => {
    sortMap.set(item.item_key, item.sort_order);
  });

  const opts = { orderItems, pricingComponents };
  return [...lines].sort((a, b) => {
    const keyA = getInvoiceLineSortKey(a, opts);
    const keyB = getInvoiceLineSortKey(b, opts);
    const orderA = getFallbackSortOrder(keyA, sortMap);
    const orderB = getFallbackSortOrder(keyB, sortMap);
    if (orderA !== orderB) return orderA - orderB;
    return keyA.localeCompare(keyB);
  });
}
